import React, { useEffect, useState } from "react";
import IconButton from "../../../iconButton";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import {
  useSaveProfessionalInfoMutation,
  useGetProfessionalInfoQuery,
} from "../../../../store/professionalInfo";
import { toast } from "react-toastify";

const defaultNotificationSettings = {
  new_lead_email_notification: 0,
  new_lead_sms_notification: 0,
  new_match_email_notification: 0,
  new_match_in_app_notification: 0,
  new_lead_in_app_notification: 0,
  new_support_email_notification: 0,
  new_support_in_app_notification: 0,
};

const AdminNotifications = ({ isMobile }) => {
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(true);
  const { data: professionalInfoData, refetch } = useGetProfessionalInfoQuery();
  const [notificationSettings, setNotificationSettings] = useState(
    defaultNotificationSettings
  );

  useEffect(() => {
    if (professionalInfoData?.data) {
      let professional = professionalInfoData?.data;
      setNotificationSettings({
        new_lead_email_notification:
          professional["new_lead_email_notification"],
        new_lead_sms_notification: professional["new_lead_sms_notification"],
        new_match_email_notification:
          professional["new_match_email_notification"],
        new_match_in_app_notification:
          professional["new_match_in_app_notification"],
        new_lead_in_app_notification:
          professional["new_lead_in_app_notification"],
        new_support_email_notification:
          professional["new_support_email_notification"],
        new_support_in_app_notification:
          professional["new_support_in_app_notification"],
      });
    }
  }, [professionalInfoData]);

  const [
    saveProfessionalInfo,
    {
      isLoading: infoIsLoading,
      isSuccess: saveIsSuccess,
      isError: saveIsError,
      error,
      data: savedProfessionalData,
    },
  ] = useSaveProfessionalInfoMutation();

  useEffect(() => {
    if (saveIsSuccess && !infoIsLoading) {
      toast.success("Notification settings updated successfully.");
      setIsSaveButtonDisabled(true);
      refetch();
    }
  }, [saveIsSuccess]);

  const handleSave = () => {
    const settings = {};
    const keys = Object.keys(notificationSettings);
    keys.forEach((key) => {
      if (notificationSettings[key] != professionalInfoData?.data[key]) {
        settings[key] = notificationSettings[key];
      }
    });

    const params = {
      _method: "PUT",
      ...settings,
    };
    saveProfessionalInfo(params);
  };

  const handleIsChange = (updateNotificationSettings) => {
    const keys = Object.keys(updateNotificationSettings);
    setIsSaveButtonDisabled(
      !keys.some(
        (key) =>
          updateNotificationSettings[key] != professionalInfoData?.data[key]
      )
    );
  };

  const handleChange = (event, type) => {
    const updateNotificationSettings = { ...notificationSettings };
    updateNotificationSettings[type] = event.target.checked ? 1 : 0;
    handleIsChange(updateNotificationSettings);
    setNotificationSettings(updateNotificationSettings);
  };

  return (
    <div>
      {!isMobile && (
        <h4 className="text-[22px] font-bold leading-[160%] text-customGray">
          Notifications
        </h4>
      )}
      <p className="text-base font-medium leading-[160%] text-[#485868] -mt-3 md:mt-auto mb-6">
        These notification settings will take effect immediately if changed. We
        recommend you enable all New Lead Notifications so you can reach out to
        leads as soon as possible. Our recommendations are the default settings,
        but you can customize them as you see fit. Transactional emails (such as
        Invoice Past Due notices) are on and cannot be turned off.
      </p>
      <div className="border border-solid border-[#EBEFF3] rounded-md p-5 bg-white hidden md:block">
        <div className="flow-root">
          <div className="overflow-x-auto">
            <form className="inline-block min-w-full align-middle">
              <table className="min-w-full divide-y divide-gray-300 notification-table">
                <thead>
                  <tr>
                    <th className="text-sm font-bold leading-[160%] text-[#485868] pb-5"></th>
                    <th className="text-sm font-bold leading-[160%] text-[#485868] pb-5">
                      Email
                    </th>
                    <th className="text-sm font-bold leading-[160%] text-[#485868] pb-5">
                      SMS
                    </th>
                    <th className="text-sm font-bold leading-[160%] text-[#485868] pb-5">
                      Web (in-app)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="text-sm font-bold leading-[160%] text-[#485868] py-5">
                      New Lead
                    </td>
                    <td className="py-5 form-group text-center">
                      <input
                        type="checkbox"
                        className="rounded border border-solid border-[#CCD0D4] relative cursor-pointer inline-block w-5 h-5 leading-[0px]"
                        checked={
                          notificationSettings.new_lead_email_notification
                        }
                        onChange={(e) =>
                          handleChange(e, "new_lead_email_notification")
                        }
                      />
                    </td>
                    <td className="py-5 form-group text-center">
                      <input
                        type="checkbox"
                        className="rounded border border-solid border-[#CCD0D4] relative cursor-pointer inline-block w-5 h-5 leading-[0px]"
                        checked={notificationSettings.new_lead_sms_notification}
                        onChange={(e) =>
                          handleChange(e, "new_lead_sms_notification")
                        }
                      />
                    </td>
                    <td className="py-5 form-group text-center">
                      <input
                        type="checkbox"
                        className="rounded border border-solid border-[#CCD0D4] relative cursor-pointer inline-block w-5 h-5 leading-[0px]"
                        checked={
                          notificationSettings.new_lead_in_app_notification
                        }
                        onChange={(e) =>
                          handleChange(e, "new_lead_in_app_notification")
                        }
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="text-sm font-bold leading-[160%] text-[#485868] py-5">
                      Support Inquiries
                    </td>
                    <td className="py-5 form-group text-center">
                      <input
                        type="checkbox"
                        className="rounded border border-solid border-[#CCD0D4] relative cursor-pointer inline-block w-5 h-5 leading-[0px]"
                        checked={
                          notificationSettings.new_match_email_notification
                        }
                        onChange={(e) =>
                          handleChange(e, "new_match_email_notification")
                        }
                      />
                    </td>
                    <td className="py-5 form-group text-center">
                      <input
                        type="checkbox"
                        className="rounded border border-solid border-[#CCD0D4] relative cursor-pointer inline-block w-5 h-5 leading-[0px]"
                      />
                    </td>
                    <td className="py-5 form-group text-center">
                      <input
                        type="checkbox"
                        className="rounded border border-solid border-[#CCD0D4] relative cursor-pointer inline-block w-5 h-5 leading-[0px]"
                        checked={
                          notificationSettings.new_match_in_app_notification
                        }
                        onChange={(e) =>
                          handleChange(e, "new_match_in_app_notification")
                        }
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="text-sm font-bold leading-[160%] text-[#485868] py-5">
                      New Enrollment
                    </td>
                    <td className="py-5 form-group text-center">
                      <input
                        type="checkbox"
                        className="rounded border border-solid border-[#CCD0D4] relative cursor-pointer inline-block w-5 h-5 leading-[0px]"
                        checked={
                          notificationSettings.new_support_email_notification
                        }
                        onChange={(e) =>
                          handleChange(e, "new_support_email_notification")
                        }
                      />
                    </td>
                    <td className="py-5 form-group text-center">
                      <input
                        type="checkbox"
                        className="rounded border border-solid border-[#CCD0D4] relative cursor-pointer inline-block w-5 h-5 leading-[0px]"
                      />
                    </td>
                    <td className="py-5 form-group text-center">
                      <input
                        type="checkbox"
                        className="rounded border border-solid border-[#CCD0D4] relative cursor-pointer inline-block w-5 h-5 leading-[0px]"
                        checked={
                          notificationSettings.new_support_in_app_notification
                        }
                        onChange={(e) =>
                          handleChange(e, "new_support_in_app_notification")
                        }
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="text-sm font-bold leading-[160%] text-[#485868] py-5">
                      New Delinquent Account
                    </td>
                    <td className="py-5 form-group text-center">
                      <input
                        type="checkbox"
                        className="rounded border border-solid border-[#CCD0D4] relative cursor-pointer inline-block w-5 h-5 leading-[0px]"
                        checked={
                          notificationSettings.new_support_email_notification
                        }
                        onChange={(e) =>
                          handleChange(e, "new_support_email_notification")
                        }
                      />
                    </td>
                    <td className="py-5 form-group text-center">
                      <input
                        type="checkbox"
                        className="rounded border border-solid border-[#CCD0D4] relative cursor-pointer inline-block w-5 h-5 leading-[0px]"
                      />
                    </td>
                    <td className="py-5 form-group text-center">
                      <input
                        type="checkbox"
                        className="rounded border border-solid border-[#CCD0D4] relative cursor-pointer inline-block w-5 h-5 leading-[0px]"
                        checked={
                          notificationSettings.new_support_in_app_notification
                        }
                        onChange={(e) =>
                          handleChange(e, "new_support_in_app_notification")
                        }
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </form>
          </div>
        </div>
      </div>
      <div className="hidden md:flex justify-end">
        <IconButton
          title="SAVE"
          icon={CheckCircleIcon}
          classes={`text-sm font-black leading-[160%] text-white ${
            infoIsLoading ? "max-w-[168px]" : "max-w-[128px]"
          } max-w-[128px] w-full flex items-center justify-center gap-[6px] bg-primary h-10 rounded-[10px] mt-5 ${
            isSaveButtonDisabled ? "opacity-50 cursor-not-allowed" : ""
          }`}
          onClick={handleSave}
          iconClasses="icon w-6 h-6 -ml-0.5 mr-[10px]"
          buttonLoader={infoIsLoading}
        />
      </div>
      <div className="block md:hidden border border-solid border-[#EBEFF3] bg-white rounded-md p-5">
        <ul>
          <li className="pb-5 border-b border-solid border-[#EBEFF3] mb-5">
            <h6 className="text-base font-bold leading-[160%] text-customGray mb-[14px]">
              New Lead{" "}
            </h6>
            <div className="flex items-center justify-between gap-7">
              <div className="form-group flex items-center gap-2.5">
                <input
                  type="checkbox"
                  className="rounded border border-solid border-[#CCD0D4] relative cursor-pointer inline-block w-5 h-5 leading-[0px]"
                  checked={notificationSettings.new_lead_email_notification}
                  onChange={(e) =>
                    handleChange(e, "new_lead_email_notification")
                  }
                />
                <label className="text-sm font-bold leading-[160%] text-[#485868]">
                  Email
                </label>
              </div>
              <div className="form-group flex items-center gap-2.5">
                <input
                  type="checkbox"
                  className="rounded border border-solid border-[#CCD0D4] relative cursor-pointer inline-block w-5 h-5 leading-[0px]"
                  checked={notificationSettings.new_lead_sms_notification}
                  onChange={(e) => handleChange(e, "new_lead_sms_notification")}
                />
                <label className="text-sm font-bold leading-[160%] text-[#485868]">
                  SMS
                </label>
              </div>
              <div className="form-group flex items-center gap-2.5">
                <input
                  type="checkbox"
                  className="rounded border border-solid border-[#CCD0D4] relative cursor-pointer inline-block w-5 h-5 leading-[0px]"
                  checked={notificationSettings.new_lead_in_app_notification}
                  onChange={(e) =>
                    handleChange(e, "new_lead_in_app_notification")
                  }
                />
                <label className="text-sm font-bold leading-[160%] text-[#485868]">
                  Web (in-app)
                </label>
              </div>
            </div>
          </li>
          <li className="pb-5 border-b border-solid border-[#EBEFF3] mb-5">
            <h6 className="text-base font-bold leading-[160%] text-customGray mb-[14px]">
              Support Inquiries
            </h6>
            <div className="flex items-center justify-between gap-7">
              <div className="form-group flex items-center gap-2.5">
                <input
                  type="checkbox"
                  className="rounded border border-solid border-[#CCD0D4] relative cursor-pointer inline-block w-5 h-5 leading-[0px]"
                  checked={notificationSettings.new_match_email_notification}
                  onChange={(e) =>
                    handleChange(e, "new_match_email_notification")
                  }
                />
                <label className="text-sm font-bold leading-[160%] text-[#485868]">
                  Email
                </label>
              </div>
              <div className="form-group flex items-center gap-2.5">
                <input
                  type="checkbox"
                  className="rounded border border-solid border-[#CCD0D4] relative cursor-pointer inline-block w-5 h-5 leading-[0px]"
                />
                <label className="text-sm font-bold leading-[160%] text-[#485868]">
                  SMS
                </label>
              </div>
              <div className="form-group flex items-center gap-2.5">
                <input
                  type="checkbox"
                  className="rounded border border-solid border-[#CCD0D4] relative cursor-pointer inline-block w-5 h-5 leading-[0px]"
                  checked={notificationSettings.new_match_in_app_notification}
                  onChange={(e) =>
                    handleChange(e, "new_match_in_app_notification")
                  }
                />
                <label className="text-sm font-bold leading-[160%] text-[#485868]">
                  Web (in-app)
                </label>
              </div>
            </div>
          </li>
          <li className="pb-5 border-b border-solid border-[#EBEFF3] mb-5 notification-table">
            <h6 className="text-base font-bold leading-[160%] text-customGray mb-[14px]">
              New Enrollment
            </h6>
            <div className="flex items-center justify-between gap-7">
              <div className="form-group flex items-center gap-2.5">
                <input
                  type="checkbox"
                  className="rounded border border-solid border-[#CCD0D4] relative cursor-pointer inline-block w-5 h-5 leading-[0px]"
                  checked={notificationSettings.new_support_email_notification}
                  onChange={(e) =>
                    handleChange(e, "new_support_email_notification")
                  }
                />
                <label className="text-sm font-bold leading-[160%] text-[#485868]">
                  Email
                </label>
              </div>
              <div className="form-group flex items-center gap-2.5">
                <input
                  type="checkbox"
                  className="rounded border border-solid border-[#CCD0D4] relative cursor-pointer inline-block w-5 h-5 leading-[0px]"
                />
                <label className="text-sm font-bold leading-[160%] text-[#485868]">
                  SMS
                </label>
              </div>
              <div className="form-group flex items-center gap-2.5">
                <input
                  type="checkbox"
                  className="rounded border border-solid border-[#CCD0D4] relative cursor-pointer inline-block w-5 h-5 leading-[0px]"
                  checked={notificationSettings.new_support_in_app_notification}
                  onChange={(e) =>
                    handleChange(e, "new_support_in_app_notification")
                  }
                />
                <label className="text-sm font-bold leading-[160%] text-[#485868]">
                  Web (in-app)
                </label>
              </div>
            </div>
          </li>
          <li className="notification-table">
            <h6 className="text-base font-bold leading-[160%] text-customGray mb-[14px]">
              New Delinquent Account
            </h6>
            <div className="flex items-center justify-between gap-7">
              <div className="form-group flex items-center gap-2.5">
                <input
                  type="checkbox"
                  className="rounded border border-solid border-[#CCD0D4] relative cursor-pointer inline-block w-5 h-5 leading-[0px]"
                  checked={notificationSettings.new_support_email_notification}
                  onChange={(e) =>
                    handleChange(e, "new_support_email_notification")
                  }
                />
                <label className="text-sm font-bold leading-[160%] text-[#485868]">
                  Email
                </label>
              </div>
              <div className="form-group flex items-center gap-2.5">
                <input
                  type="checkbox"
                  className="rounded border border-solid border-[#CCD0D4] relative cursor-pointer inline-block w-5 h-5 leading-[0px]"
                />
                <label className="text-sm font-bold leading-[160%] text-[#485868]">
                  SMS
                </label>
              </div>
              <div className="form-group flex items-center gap-2.5">
                <input
                  type="checkbox"
                  className="rounded border border-solid border-[#CCD0D4] relative cursor-pointer inline-block w-5 h-5 leading-[0px]"
                  checked={notificationSettings.new_support_in_app_notification}
                  onChange={(e) =>
                    handleChange(e, "new_support_in_app_notification")
                  }
                />
                <label className="text-sm font-bold leading-[160%] text-[#485868]">
                  Web (in-app)
                </label>
              </div>
            </div>
          </li>
        </ul>
      </div>
      <div className="flex md:hidden justify-end">
        <IconButton
          title="SAVE"
          icon={CheckCircleIcon}
          classes={`text-sm font-black leading-[160%] text-white w-full flex items-center justify-center gap-[6px] bg-primary h-10 rounded-[10px] mt-5 ${
            isSaveButtonDisabled ? "opacity-50 cursor-not-allowed" : ""
          }`}
          // onClick={handleSave}
          iconClasses="icon w-6 h-6 -ml-0.5 mr-[10px]"
          buttonLoader={infoIsLoading}
        />
      </div>
    </div>
  );
};

export default AdminNotifications;
